/* Created by Interactive 63 - https://interactive63.com */
/**
 * The timeline's row and keyframe model. Rows are built from the project, the selection and the
 * collapsed set; marks are the drawable keyframes on those rows. Pure data, so it can be tested
 * without a canvas.
 */
import { listOrder } from '../../../core/model/order';
import type { Easing, ElementId, Project, PropertyKey, Track } from '../../../core/model/types';
import { PROPERTIES } from '../../../core/properties/registry';

export const TRACK_ROW_HEIGHT = 26;
export const ELEMENT_ROW_HEIGHT = 28;

export type MarkShape = 'diamond' | 'square' | 'circle';

export interface TimelineRow {
  key: string;
  kind: 'element' | 'track';
  elementId: ElementId;
  track?: Track;
  y: number;
  height: number;
}

export interface KeyMark {
  /** Keyframe ID for track marks; the first ID for merged marks. */
  id: string;
  /** Every keyframe this mark stands for (several for merged element-row marks). */
  ids: string[];
  rowIndex: number;
  time: number;
  merged: boolean;
  shape: MarkShape;
  /** The outgoing easing is a hold, so the line to the next keyframe is dashed. */
  hold: boolean;
  elementId: ElementId;
  property?: PropertyKey;
  trackEnabled: boolean;
}

const PROPERTY_ORDER = Object.keys(PROPERTIES);

/** The mark shape that signals the outgoing easing. */
export function shapeForEasing(easing: Easing): MarkShape {
  switch (easing.type) {
    case 'hold':
    case 'steps':
      return 'square';
    case 'spring':
    case 'bounce':
    case 'elastic':
      return 'circle';
    default:
      return 'diamond';
  }
}

/**
 * Builds the rows: one per animated or selected element in layer list order, each followed by its
 * tracks in property order unless the element is collapsed.
 */
export function buildRows(
  project: Project,
  selectedElementIds: readonly ElementId[],
  collapsed: ReadonlySet<ElementId>,
): TimelineRow[] {
  const ids = new Set<ElementId>(selectedElementIds.filter((id) => id in project.elements));
  for (const track of project.tracks) ids.add(track.elementId);

  const rows: TimelineRow[] = [];
  let y = 0;
  const order = listOrder(project);
  for (const elementId of [...ids].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0))) {
    rows.push({ key: elementId, kind: 'element', elementId, y, height: ELEMENT_ROW_HEIGHT });
    y += ELEMENT_ROW_HEIGHT;
    if (collapsed.has(elementId)) continue;
    const tracks = project.tracks
      .filter((t) => t.elementId === elementId)
      .sort((a, b) => PROPERTY_ORDER.indexOf(a.property) - PROPERTY_ORDER.indexOf(b.property));
    for (const track of tracks) {
      rows.push({
        key: `${elementId}:${track.property}`,
        kind: 'track',
        elementId,
        track,
        y,
        height: TRACK_ROW_HEIGHT,
      });
      y += TRACK_ROW_HEIGHT;
    }
  }
  return rows;
}

/** Total height of the rows. */
export function totalHeight(rows: readonly TimelineRow[]): number {
  const last = rows[rows.length - 1];
  return last ? last.y + last.height : 0;
}

/** Finds the row index at a y position, or -1. */
export function rowAtY(rows: readonly TimelineRow[], y: number): number {
  return rows.findIndex((row) => y >= row.y && y < row.y + row.height);
}

/**
 * Builds the keyframe marks for the rows. Element rows get one merged mark per distinct time,
 * standing for every keyframe at that time on the element; track rows get one mark each.
 */
export function buildMarks(project: Project, rows: readonly TimelineRow[]): KeyMark[] {
  const marks: KeyMark[] = [];
  rows.forEach((row, rowIndex) => {
    if (row.kind === 'track' && row.track) {
      const { track } = row;
      for (const kf of track.keyframes) {
        marks.push({
          id: kf.id,
          ids: [kf.id],
          rowIndex,
          time: kf.time,
          merged: false,
          shape: shapeForEasing(kf.easing),
          hold: kf.easing.type === 'hold',
          elementId: row.elementId,
          property: track.property,
          trackEnabled: track.enabled,
        });
      }
      return;
    }
    const byTime = new Map<number, string[]>();
    for (const track of project.tracks) {
      if (track.elementId !== row.elementId) continue;
      for (const kf of track.keyframes) {
        byTime.set(kf.time, [...(byTime.get(kf.time) ?? []), kf.id]);
      }
    }
    for (const [time, ids] of [...byTime].sort((a, b) => a[0] - b[0])) {
      marks.push({
        id: ids[0] ?? '',
        ids,
        rowIndex,
        time,
        merged: true,
        shape: 'diamond',
        hold: false,
        elementId: row.elementId,
        trackEnabled: true,
      });
    }
  });
  return marks;
}
